/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  FC,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import { NextRouter, useRouter } from 'next/router';
import * as yup from 'yup';
import { DevTool } from '@hookform/devtools';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  FieldValues,
  FormProvider as RHFormProvider,
  useForm,
  UseFormHandleSubmit,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form';
import Toast from '@/components/Toast';
import { getEnvironmentDetails } from '@/utils/env';

type TFieldValuesMergeUnion<U> = (
  U extends any ? (__k: U) => void : never
) extends (__k: infer I) => void
  ? I
  : never;

type TStepNumberFieldValue = { stepNumber: number };
type TFieldValuesFromSteps<TFormSteps extends TFormStep[]> =
  TFieldValuesMergeUnion<TFormSteps[number]['defaultValues']> &
    TStepNumberFieldValue;

type TRouterParams = {
  as?: Parameters<NextRouter['push']>[1];
  options?: Parameters<NextRouter['push']>[2];
};

//---------------------------------------------------------------------
export type TFormStep<TFieldValues extends FieldValues = any> = {
  schema: Record<keyof TFieldValues, yup.AnySchema>;
  defaultValues: TFieldValues;
  nextStepNumber?: number | null; //null means it is last step
  Component: React.ComponentType<Pick<ChildrenProps<TFieldValues>, 'onSubmit'>>;
  onSubmit?: TFormStepOnSubmit<TFieldValues>;
  onSubmitError?: (
    __e: any,
    __defaultOnSubmitError: (__e?: any) => void
  ) => void;
  routerParams?: TRouterParams;
};
export type ChildrenProps<TFieldValues extends FieldValues> =
  UseFormReturn<TFieldValues> &
    TStepNumberFieldValue & {
      stepsCount: number;
      Component: React.ComponentType;
      onSubmit: ReturnType<UseFormHandleSubmit<TFieldValues>>;
    };

export type TFormStepOnSubmit<TFieldValues extends FieldValues> = (
  __values: TFieldValues,
  __formReturn: UseFormReturn<TFieldValues>
) => void | Promise<void>;

export type TFormsProviderFunctionalChildren<
  TFieldValues extends FieldValues = {},
> = (__props: ChildrenProps<TFieldValues>) => JSX.Element;

export type TFormsProviderProps<TFormSteps extends TFormStep[]> = {
  forms: TFormSteps; // steps: TFormSteps;
  className?: string;
  children?:
    | TFormsProviderFunctionalChildren<TFieldValuesFromSteps<TFormSteps>>
    | ReactNode;
  initialStepNumber?: number;
  onComplete?: TFormStepOnSubmit<TFieldValuesFromSteps<TFormSteps>>;
  defaultRouterOptions?: TRouterParams['options'];
} & Partial<Omit<UseFormProps<TFieldValuesFromSteps<TFormSteps>>, 'resolver'>>;

const FormsProvider = <TFormSteps extends TFormStep[] = TFormStep[]>({
  forms: steps,
  children,
  className,
  initialStepNumber = 1,
  onComplete,
  defaultRouterOptions,
  ...props
}: TFormsProviderProps<TFormSteps>): JSX.Element => {
  type TFieldValues = TFieldValuesFromSteps<TFormSteps>;

  const { push, replace, query } = useRouter();

  const getStep = (stepNumber: number): TFormStep => steps[stepNumber - 1];
  const isStepNumberValid = (stepNumber: number): boolean => {
    return isMultiStep && stepNumber <= stepsCount;
  };
  const saveStepNumber = async (stepNumber: number): Promise<void> => {
    formReturn.setValue('stepNumber', stepNumber, { shouldValidate: true });
  };
  const gotoStepNumber = async (stepNumber: number): Promise<void> => {
    const { routerParams } = getStep(stepNumber);
    await push({ query: { ...query, step: stepNumber } }, routerParams?.as, {
      shallow: true,
      ...(defaultRouterOptions || {}),
      ...(routerParams?.options || {}),
    });
  };

  const formReturn = useForm({
    mode: 'all',
    ...props,
    resolver: yupResolver(
      yup.object({ stepNumber: yup.number() }).when((values, schema) => {
        return schema.concat(yup.object(getStep(values.stepNumber).schema));
      })
    ),
    defaultValues: useMemo(() => {
      return steps.reduce(
        (defaultValues, step) => {
          return Object.assign(defaultValues, step.defaultValues);
        },
        { stepNumber: initialStepNumber }
      );
    }, []) as any,
  });

  const stepNumber: number = formReturn.watch('stepNumber');
  const queryStepNumber: number = +query?.step! || stepNumber;
  const step = getStep(stepNumber);
  const stepsCount: number = steps.length;
  const isMultiStep: boolean = stepsCount > 1;

  useLayoutEffect(() => {
    // eslint-disable-next-line no-unused-vars
    const { step, ...restQuery } = query;
    const newQuery = {
      ...restQuery,
      ...(isMultiStep && { step: initialStepNumber }),
    };
    replace({ query: newQuery }, undefined, { shallow: true }).then();
  }, []);

  useEffect(() => {
    if (queryStepNumber !== stepNumber && isStepNumberValid(queryStepNumber)) {
      saveStepNumber(queryStepNumber).then();
    }
  }, [queryStepNumber]);

  useEffect(() => {
    if (stepNumber !== queryStepNumber && isStepNumberValid(stepNumber)) {
      gotoStepNumber(stepNumber).then();
    }
  }, [stepNumber]);

  const onSubmit = formReturn.handleSubmit(
    async (values: TFieldValues) => {
      try {
        await step?.onSubmit?.(values, formReturn);
        if (step?.nextStepNumber !== null) {
          const nextStepNumber: number = step?.nextStepNumber || stepNumber + 1;
          if (nextStepNumber > stepsCount)
            await onComplete?.(values, formReturn);
          else await gotoStepNumber(nextStepNumber);
        }
      } catch (error: any) {
        const onSubmitError = (e = error) =>
          Toast.open({ variant: 'error', children: `${e?.message || e}` });
        if (typeof step?.onSubmitError === 'function') {
          step?.onSubmitError(error, onSubmitError);
        } else onSubmitError();
      }
    },
    (fieldsError) => {
      const errorMessages: string[] = Object.values(fieldsError).map(
        (fieldError) => fieldError?.message as string
      );
      Toast.open({
        variant: 'warning',
        className: 'text-right',
        children: errorMessages.map((errorMessages, index) => {
          return <div key={index}>{errorMessages}</div>;
        }),
      });
    }
  );

  const childrenProps: Parameters<
    TFormsProviderFunctionalChildren<TFieldValues>
  >[number] = {
    ...(formReturn as UseFormReturn<TFieldValues>),
    onSubmit,
    stepNumber,
    stepsCount,
    Component: useMemo(() => {
      const StepComponent: FC<PropsWithChildren<any>> = (props) => (
        <step.Component {...props} onSubmit={onSubmit} />
      );
      StepComponent.displayName = `Step${stepNumber}Component`;
      return StepComponent;
    }, [stepNumber]),
  };

  return (
    <RHFormProvider {...formReturn}>
      {getEnvironmentDetails().isDevelopmentEnvironment && (
        <div style={{ direction: 'ltr' }}>
          <DevTool control={formReturn.control} />
        </div>
      )}
      <form className={`h-full ${className || ''}`} onSubmit={onSubmit}>
        {typeof children === 'function' ? (
          children(childrenProps)
        ) : (
          <>
            <childrenProps.Component />
            {children}
          </>
        )}
      </form>
    </RHFormProvider>
  );
};

export default FormsProvider;
